import { IClientProject, IClientKeyUpdate, IClientKey, IClientUpdateProject, IClientChangeProjectUser, IClientCreateProject, IClientChangeProjectBillingConsumption, IClientProjectInvitation, IClientAdminMessages, IClientProjectCatalogPayload } from "./client-interfaces";
import { IAdminProject, IAdminKeyUpdate, IAdminKey, IAdminUpdateProject, IAdminChangeProjectUser, IAdminCreateProject, IAdminChangeProjectBillingConsumption, IAdminAdminMessages } from "./admin-interfaces";

export type TUserKeyRole = "owner" | "admin" | "visitor";
export type TUserSource = "rus";
export type TInternalUserRole = "none" | "internal_admin" | "salesman";
export type TRestrictionsTypes = "ipAddresses" | "referers" | "userAgents" | "";
export type TProjectBusinessPlan = "basic" | "advanced" | "vip" | "technical" | "internal";
export type TProjectBillingMethod = "none" | "wallet" | "sos" | "barter";
export type TServiceType = "ROOT" | "ENDPOINT";
export type IApiCall<CLIENT = any, ADMIN = any> = {
	client: CLIENT;
	admin: ADMIN;
};

export enum ELoginState {
	Green = 'green',
	Orange = 'orange',
	Red = 'red',
}

export interface IUserState {
	name: string;
	email: string;
	rusId: number;
	state: ELoginState;
	avatar: string;
	publicId: string;
	internalRole: TInternalUserRole;
	isInternal: boolean;
	hasProjects: boolean;
	userLanguage: string;
}

export interface IUser {
	id: number;
	source: string;
	email: string;
	createdAt: string;
	role: TUserKeyRole;
	emailNotifications: boolean;
	userLanguage: string;
}

export interface IUserAdmin extends IUser {
	banned: boolean;
	maxProjectCount: number;
	projectCountDescription: string;
}

export interface IConsumption {
	id: number;
	date: string;
	service: string;
	count: number;
	summary: boolean;
	callPrice: number;
}

export interface ITransferOwnership {
	newOwnerEmail: string;
	lastUpdate: string;
}

export interface IProjectOutput extends IApiCall<IClientProject, IAdminProject> {}

export interface IProjectsOutput extends IApiCall<Array<IClientProject>, Array<IAdminProject>> {}

export interface IKeyOutput extends IApiCall<IClientKey, IAdminKey> {}

export interface IKeysOutput extends IApiCall<Array<IClientKey>, Array<IAdminKey>> {}

export interface IProjectUserOutput extends IApiCall<IUser, IUserAdmin> {}

export interface IProjectUsersOutput extends IApiCall<Array<IUser>, Array<IUserAdmin>> {}

export interface IProjectConsumptionOutput extends IApiCall<IConsumption, IConsumption> {}

export interface IProjectConsumptionsOutput extends IApiCall<Array<IConsumption>, Array<IConsumption>> {}

/* POST, PUT globalni interface */
export interface IUpdateKey extends IApiCall<IClientKeyUpdate, IAdminKeyUpdate> {}

export interface IUpdateProject extends IApiCall<IClientUpdateProject, IAdminUpdateProject> {}

export interface IUpdateProjectBillingConsumption extends IApiCall<IClientChangeProjectBillingConsumption, IAdminChangeProjectBillingConsumption> {}

export interface IChangeProjectUser extends IApiCall<IClientChangeProjectUser, IAdminChangeProjectUser> {}

export interface IProjectAdminMessagesOutput extends IApiCall<IClientAdminMessages, IAdminAdminMessages> {}

export interface ILastUpdateOutput {
	lastUpdate: string;
}

export interface IService {
	id: number;
	service: string;
	callPrice: number;
	serviceType: TServiceType;
	hiddenForUsers: boolean;
	regularService: boolean;
	rootServiceId: null | number;
}

export interface IServiceLittle extends Pick<IService, "id" | "callPrice" | "service"> {}

export interface IUpdateKeyServices {
	services: Array<number>;
	lastUpdate: string;
}

export interface ICreateService extends Omit<IService, "id"> {}

export interface IEditServiceData extends Pick<IService, "callPrice" | "hiddenForUsers" | "regularService"> {}

export interface IInternalUser {
	id: number;
	source: TUserSource;
	email: string;
	createdAt: string;
	internalRole: TInternalUserRole;
}

export interface ICreateInternalUser {
	email: string;
	internalRole: Omit<TInternalUserRole, "none">;
}

export interface IUpdateInternalUser extends Pick<ICreateInternalUser, "internalRole"> {}

export interface IProjectAddUser {
	email: string;
	role: TUserKeyRole;
	emailNotifications: boolean;
	userLanguage: string;
	lastUpdate: string;
}

export interface IInvite extends Pick<IClientProjectInvitation, "invitedEmail" | "invitedRole"> {
	emailLanguage: string;
	lastUpdate: string;
}

export interface ICreateProject extends IApiCall<IClientCreateProject, IAdminCreateProject> {}

export interface ICreateProjectWithKey extends IApiCall<{
	data: IClientProject;
	dataKey: IClientKey;
	status: number;
	noErrors: boolean;
	errors: Array<string>;
}, {
	data: IAdminProject;
	dataKey: IAdminKey;
	status: number;
	noErrors: boolean;
	errors: Array<string>;
}> {}

export interface IProjectBillingHistory extends Pick<IAdminProject, "businessPlanType" | "billingMethod" | "fixedBillingAmount" | "pricePerCredit"> {
	id: number;
	billingPeriodFrom: string;
	billingPeriodTill: string;
	totalConsumedCredits: number;
	creditsFreeOfCharge: number;
	creditsCharged: number;
	amountTotal: number;
	debt: number;
	transactionState: "completed" | "partial" | "unprocessed";
}

export interface ITransactionApproval extends IProjectBillingHistory, Pick<IAdminProject, "totalConsumptionLimit"> {
	transactionId: string;
	requestedAt: string;
	projectId: number;
	projectInternalName: string;
	billingHistoryId: number;
	walletId: number;
	walletEmail: string;
	approvalReason: "limit" | "debt";
}

export interface IAdvancedBillingConsent {
	acceptedByEmail: string;
	acceptedAt: string;
	validTill: string;
	projectAddress: string;
	projectDescription: string;
}

export interface ISuggestProjectName {
	internalName: string;
}

export interface IUpdateProjectBillingConsumptionOutput extends IApiCall<IClientChangeProjectBillingConsumption, IAdminChangeProjectBillingConsumption> {}

export interface IChanceProjectNotificationData {
	emailNotifications: boolean;
	lastUpdate: string;
}

export interface IReportItem {
	periodStart: string;
	periodEnd: string;
	newProjectCount: number;
	projectsWithConsumption: number;
	projectsBasicWithConsumption: number;
	projectsVipWithConsumption: number;
	projectsAdvancedWithConsumption: number;
	totalConsumedCredits: number;
	creditsFreeOfCharge: number;
	creditsCharged: number;
	amountTotal: number;
	amountViaWallet: number;
	amountViaSos: number;
	amountViaBarter: number;
	totalResourceCalls: number;
	[key: string]: number | string;
}

export type TCatalogQuality = "vip" | "interesting" | "neutral" | "uninteresting" | "undetermined";

export interface ICatalogItem {
	id: string;
	internalName: string;
	previousMonthConsumption: number;
	projectId: number;
	published: boolean;
	approvedByAdmin: boolean | null;
	quality: TCatalogQuality;
	referenceUrl: string;
	thumbnailUrl: string | null;
	createdAt: string;
	lastUpdate: string;
}

export interface IProjectCatalogUpdateData {
	referenceUrl: string;
	payload: Array<IClientProjectCatalogPayload>;
}

export interface IProjectCatalogAdminSettingsData {
	approvedByAdmin: boolean | null;
	quality: TCatalogQuality;
}
